import { useArchiveProcessMap } from 'api/services/processMapService';
import { GenericDeleteModal } from 'components/ui/GenericDeleteModal';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useUI } from 'contexts/UiContext';
import { useTranslation } from 'react-i18next';
import { ErrorType } from 'types/genericTypes';
import { useQueryClient } from '@tanstack/react-query';

interface ProcessMapArchiveModalProps {
  open: boolean;
  onCancel: () => void;
  processMapId?: string;
  name?: string;
  isArchive: boolean;
}

export const ProcessMapArchiveModal = ({
  open,
  onCancel,
  processMapId,
  name,
  isArchive,
}: ProcessMapArchiveModalProps) => {
  const { t } = useTranslation();
  const { selectedCompany } = useUI();
  const { openNotification } = useFloatNotification();
  const queryClient = useQueryClient();
  const {
    mutateAsync: archiveProcessMap,
    isPending: isPendingArchiveProcessMap,
  } = useArchiveProcessMap();

  const handleConfirm = async () => {
    try {
      await archiveProcessMap({
        companyId: selectedCompany || '',
        traceabilityId: processMapId || '',
        payload: { isArchived: isArchive },
      });
      openNotification(
        t('success'),
        t(
          isArchive
            ? 'process-map.archive-modal.success'
            : 'process-map.unarchive-modal.success'
        ),
        'success'
      );

      // Invalidate and refetch the process maps query to update archived count
      queryClient.invalidateQueries({
        queryKey: ['processMaps', selectedCompany, false],
      });

      onCancel();
    } catch (error) {
      const apiError = error as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t(
            isArchive
              ? 'process-map.archive-modal.on-error'
              : 'process-map.unarchive-modal.on-error'
          ),
          'error'
        );
      }
    }
  };

  return (
    <GenericDeleteModal
      open={open}
      onCancel={onCancel}
      onOk={handleConfirm}
      confirmLoading={isPendingArchiveProcessMap}
      bodyTitle={t(
        isArchive
          ? 'process-map.archive-modal.title'
          : 'process-map.unarchive-modal.title'
      )}
      bodyText={t(
        isArchive
          ? 'process-map.archive-modal.text'
          : 'process-map.unarchive-modal.text',
        {
          processName: name,
        }
      )}
      okTitle={t(
        isArchive
          ? 'process-map.archive-modal.confirm'
          : 'process-map.unarchive-modal.confirm'
      )}
    />
  );
};
